/**
 * Cookie consent types for GDPR compliance
 */

export type ConsentCategory = "necessary" | "analytics" | "marketing";

export interface ConsentState {
  /** Version of the consent schema (for migrations) */
  version: string;
  /** Timestamp of when consent was given/updated */
  timestamp: number;
  /** Whether user has made a choice */
  hasConsented: boolean;
  /** Per-category consent */
  categories: Record<ConsentCategory, boolean>;
}

export interface ConsentContextValue {
  /** Current consent state */
  consent: ConsentState;
  /** Whether consent has been loaded from storage */
  isLoaded: boolean;
  /** Accept all categories */
  acceptAll: () => void;
  /** Reject all non-essential categories */
  rejectNonEssential: () => void;
  /** Update a specific category */
  setCategory: (category: ConsentCategory, value: boolean) => void;
  /** Update multiple categories */
  setCategories: (categories: Partial<Record<ConsentCategory, boolean>>) => void;
  /** Reset consent (for testing/debugging) */
  reset: () => void;
  /** Whether a specific category is consented */
  isConsented: (category: ConsentCategory) => boolean;
}

/** Initial/default consent state - all false until user decides */
export const DEFAULT_CONSENT_STATE: ConsentState = {
  version: "1.0",
  timestamp: 0,
  hasConsented: false,
  categories: {
    necessary: true, // Always true - cannot be disabled
    analytics: false,
    marketing: false,
  },
};

/** Local storage key for consent */
export const CONSENT_STORAGE_KEY = "tutuly-consent";

/** Cookie categories with descriptions for UI */
export const CONSENT_CATEGORIES: { id: ConsentCategory; required: boolean }[] = [
  { id: "necessary", required: true },
  { id: "analytics", required: false },
  { id: "marketing", required: false },
];
